import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import LoaderIcon from '../../components/loaderIcon/LoaderIcon';
import { Button, Typography } from '@mui/material';
import SelectTicketView from '../selectTicketView/SelectTicketView';
import Footer from '../../components/footer/Footer';
import ErrorModal from '../../components/errormodal/ErrorModal';
import Spinner from '../../components/spinner/Spinner';
import ViewTicketDetailPageContext, {
	IViewTicketDetailPageContext,
} from '../../Contexts/ViewTicketPageContext/ViewTicketPageContext';
import PreAuthPageContext, { IPreAuthItemContext } from '../../Contexts/PreAuthPageContext/PreAuthPageContext';

import { IModifierDetails, IModifierSetDetails, IOrderDetailItems } from '../home/models/orderDetailModels';

import Toast from '../../components/toast/Toast';
import AppContext, { IAppContext } from '../../Contexts/AppContext/AppContext';
import OrderDetailPageContext, {
	IOrderDetailContext,
} from '../../Contexts/OrderDetailPageContext/OrderDetailPageContext';
import { getFormattedPrice, dollarsToCents } from '../../components/helper/common';
import * as labels from './labels';
import orderDetailStyles from './OrderDetail.module.scss';
import { constructPlaceOrderRequestBody } from '../../utils/orderUtils';
import MenuItemsPageContext, { IMenuItemContext } from '../../Contexts/MenuItemsPageContext/MenuItemsPageContext';
import OrderConfirmPageContext, {
	IOrderConfirmContext,
} from '../../Contexts/OrderConfirmPageContext/OrderConfirmPageContext';
import { IViewTabItem } from '../viewTickets/models/ticketDetailsModels';


const OrderDetail: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const isOpenTabView = location.pathname.includes('viewOpenTab');
	const [isOrderDetailViewActive, setIsOrderDetailViewActive] = useState(true);
	const [apiErrorMessage, setApiErrorMessage] = useState({ MESSAGE: '', TITLE: '', STATUS: 0 });
	const { setIsActiveSession } = useContext(PreAuthPageContext) as IPreAuthItemContext;
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [payView, setPayView] = useState<boolean>(false);
	const [totalPriceInCart, setTotalPriceInCart] = useState<number>(0);
	const {
		isValidSession,
		sessionErrorStatus,
		ticketRefDetail,
		setTicketRefDetail,
		toastMessage,
		updateToastMessage,
		showLoaderIcon,
		setShowLoaderIcon,
		validSessionId,
	} = useContext(AppContext) as IAppContext;
	const { getQRPayHost } = useContext(OrderConfirmPageContext) as IOrderConfirmContext;
	const { getTicket, ticketDetails, setShouldRedirectToViewCheck, setTicketDetails } = useContext(
		ViewTicketDetailPageContext
	) as IViewTicketDetailPageContext;
	const { postOrderDetail } = useContext(OrderDetailPageContext) as IOrderDetailContext;
	const { merchantData } = useContext(MenuItemsPageContext) as IMenuItemContext;
	const tableRefId = localStorage.getItem('tableRef');
	const merchantLogo = localStorage.getItem('merchantLogo');
	const [viewTabDetails, setviewTabDetails] = useState<IViewTabItem[]>([]);
	const [orderDetail, setOrderDetail] = useState({
		shopName: '',
		customerName: '',
		table: '',
		order: {
			items: [
				{
					menuItemGuid: '',
					specialRequest: '',
					itemName: '',
					itemPrice: '',
					quantity: 1,
					modSets: [],
				},
			],
			totalPrice: '',
		},
	});
	const { storeId } = useParams<{ storeId: string }>();
	const order = localStorage.getItem('orderDetail');
	const existingSessionId = localStorage.getItem('storedSessionId');
	const isQrPayEnabled = localStorage.getItem('isQRPayEnabled') === 'true';
	const existingTicketRef = localStorage.getItem('ticketRefDetail');
	const existingTableRef = localStorage.getItem('tableRef');

	useEffect(() => {
		window.scrollTo(0, 0);
		if (!existingTableRef && !order) {
			setShowLoaderIcon(false);
			setApiErrorMessage(labels.BROWSER_ERRORS);
		}
		if (order) {
			localStorage.setItem('cartItems', JSON.stringify(JSON.parse(order).order));
		}
		if (existingTicketRef) {
			setTicketRefDetail(JSON.parse(existingTicketRef));
		}
		if (isOpenTabView && existingSessionId && storeId) {
			getTicket(storeId, existingSessionId, false);
			return;
		}

		updateToastMessage('');

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (orderDetail?.order?.items?.length === 0) {
			localStorage.removeItem('orderDetail');
			localStorage.removeItem('cartItems');
			localStorage.removeItem('ticketRefDetail');
			navigate(`/${storeId}/homePage`);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderDetail]);

	useEffect(() => {
		if (payView && ticketDetails?.amountPaid) {
			if (Number(ticketDetails?.amountPaid > 0)) {
				updateToastMessage(labels.PARTIAL_PAYMENT_ERROR_MESSAGE);
				return;
			}
			if (isValidSession && validSessionId && storeId) {
				getQRPayHost(storeId, validSessionId);
			}
			setPayView(false);
			setIsLoading(false);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ticketDetails, payView, isValidSession]);

	useEffect(() => {
		if (isOpenTabView) {
			if (ticketDetails && Array.isArray(ticketDetails.items)) {
				const mappedItems: IViewTabItem[] = (ticketDetails.items ?? []).map((item) => ({
					guid: '',
					name: item.name ?? '',
					price: item.price ?? '',
					priceNoDiscount: '',
					quantity: item.quantity ?? '',
					modifiers: item.modifiers ?? '',
					discounts: [],
					specialRequests: item.specialRequests ?? [],
					isOpenItem: false,
					isReorderable: true,
					menuItemGuid: '',
				}));
				setviewTabDetails(mappedItems);
			}
		} else if (order) {
			setOrderDetail(JSON.parse(order));
			const { order: orderDetails } = JSON.parse(order);
			if (ticketRefDetail?.tickets && ticketRefDetail?.tickets?.length > 0) {
				setIsOrderDetailViewActive(false);
			} else {
				setIsOrderDetailViewActive(true);
			}
			if (orderDetails?.totalPrice >= 0 && orderDetails?.items?.length > 0) {
				setTotalPriceInCart(Number(orderDetails?.totalPrice));
			}
		}

		if (!isValidSession && sessionErrorStatus) {
			const apiError = labels.API_ERRORS;
			const apiErrorIndex = apiError.findIndex((error) => error.STATUS === sessionErrorStatus);
			if (apiErrorIndex !== -1) {
				setApiErrorMessage(apiError[apiErrorIndex]);
				return;
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ticketRefDetail, isOpenTabView]);

	const placeOrder = async () => {
		setIsLoading(true);
		if (orderDetail && storeId) {
			const orderDetailRequestBody = constructPlaceOrderRequestBody(orderDetail);
			const orderDetailResponseObject = await postOrderDetail(storeId, orderDetailRequestBody);
			if (orderDetailResponseObject?.error && [500, 400, 404].includes(orderDetailResponseObject.error.status)) {
				updateToastMessage(orderDetailResponseObject.error.message);
			} else {
				const selectedPreviousTicketRef = localStorage.getItem('selectedPreviousTicketRef');
				if (!selectedPreviousTicketRef && orderDetailResponseObject.order?.ticketGuid) {
					localStorage.setItem('selectedPreviousTicketRef', orderDetailResponseObject.order.ticketGuid);
				}
				localStorage.removeItem('cartItems');
				if (existingSessionId && storeId) {
					getTicket(storeId, existingSessionId, false);
				}
				navigate(`/${storeId}/OrderConfirmView`);
			}
		}
		setTimeout(() => setIsLoading(false), 3000);
	};
	const handleBackClick = () => {
		navigate(`/${storeId}?tableRef=${tableRefId}`);
	};

	const modifiersDom = (modifiers: IModifierDetails, modifiersDomIndex: number) => {
		return (
			<div key={modifiersDomIndex}>
				{modifiers.menuModGuid && modifiers.menuModName && (
					<div className={orderDetailStyles.ticketRow}>
						<div className={orderDetailStyles.modifiersDomLabel}>{modifiers.menuModName}</div>
						<div className={orderDetailStyles.modifiersTicketRowValue}>
							{Number(modifiers.modifierPrice) !== 0 &&
								merchantData?.currency &&
								`${getFormattedPrice(Number(modifiers.modifierPrice), merchantData?.currency)}`}
						</div>
					</div>
				)}
				{modifiers.modSets &&
					modifiers.modSets?.length >= 0 &&
					modifiers.modSets.map((modifierSet3: IModifierSetDetails, modifierSet3Index: number) => {
						return modifierSetDom(modifierSet3, modifierSet3Index);
					})}
			</div>
		);
	};

	const modifierSetDom = (modifierSet: IModifierSetDetails, modifierSetIndex: number) => {
		return (
			<div className={orderDetailStyles.modifierSetDomContainer} key={modifierSetIndex}>
				<div className={orderDetailStyles.ticketRow}>
					<div className={orderDetailStyles.modifierSetDomLabel}>{modifierSet.menuModSetName}</div>
				</div>

				{modifierSet?.mods &&
					modifierSet?.mods?.length >= 0 &&
					modifierSet?.mods.map((modifierSet2: IModifierDetails, modifiersDomIndex: number) => {
						return modifiersDom(modifierSet2, modifiersDomIndex);
					})}
			</div>
		);
	};

	const itemsRenderDom = (itemDetails: IOrderDetailItems | IViewTabItem, itemDetailIndex: number) => {
		const quantity = isOpenTabView
			? parseFloat(itemDetails.quantity?.toString() ?? '').toString()
			: itemDetails.quantity;

		const name = "itemName" in itemDetails
			? (itemDetails as IOrderDetailItems).itemName
			: (itemDetails as IViewTabItem).name;

		if (!merchantData?.currency) {
			return null;
		}

		const price = isOpenTabView
			? getFormattedPrice(dollarsToCents((itemDetails as IViewTabItem).price ?? 0), merchantData?.currency)
			: getFormattedPrice(Number((itemDetails as IOrderDetailItems).itemPrice), merchantData?.currency);

		const modifiersContent = "modSets" in itemDetails
			? itemDetails.modSets?.map((modSet, index) => modifierSetDom(modSet, index))
			: itemDetails.modifiers && itemDetails.modifiers.length > 0 && (
				<div className={orderDetailStyles.modifierSetDomContainer}>
					<div className={orderDetailStyles.ticketRow}>
						<div className={orderDetailStyles.modifierSetDomLabel}>
							{itemDetails.modifiers}
						</div>
					</div>
				</div>
			);

		return (
			<Fragment key={itemDetailIndex}>
				<div className={orderDetailStyles.ticketRowMain}>
					<div className={orderDetailStyles.ticketRowLabelBold}>
						<div className={orderDetailStyles.itemQuantity}>{quantity}</div>
						<div className={orderDetailStyles.itemName}>{name}</div>
					</div>
					{price !== null && <div className={orderDetailStyles.ticketRowValue}>{price}</div>}
				</div>

				{modifiersContent}

				{!isOpenTabView && (
					<div className={orderDetailStyles.removeIcon}>
						<Button
							className={orderDetailStyles.removeItmeButton}
							onClick={() => handleRemoveItem(itemDetails as IOrderDetailItems, itemDetailIndex)}
						>
							Remove
						</Button>
					</div>
				)}

				<div className={orderDetailStyles.divider} />
			</Fragment>
		);
	};

	const handleErrorClick = () => {
		if (sessionErrorStatus === 404) {
			navigate(`/${storeId}/startSession`);
		} else {
			navigate(`/${storeId}/homePage`);
		}
	};

	const handleOrderView = () => {
		setShouldRedirectToViewCheck(false);
		setIsActiveSession(false);
		navigate(`/${storeId}?tableRef=${tableRefId}`);
	};

	const handlePayView = async () => {
		setIsLoading(true);
		setTicketDetails({});
		if (existingSessionId && storeId) {
			getTicket(storeId, existingSessionId, false);
			setPayView(true);
		}
	};

	const handleRemoveItem = (removeItem: IOrderDetailItems, itemDetailIndex: number) => {
		let modifierPrice = 0;
		const itemsInCart = localStorage.getItem('cartItems');
		const merchantName = localStorage.getItem('merchantName');
		const tableRef = localStorage.getItem('tableRef');

		if (itemsInCart) {
			let pendingItems = JSON.parse(itemsInCart);
			let updatedPrice = Number(pendingItems.totalPrice);
			const itemQuantity = Number(removeItem.quantity);
			let removedItemPrice = Number(removeItem.itemPrice);
			removedItemPrice = removedItemPrice * itemQuantity;
			if (pendingItems?.items[itemDetailIndex]?.menuItemGuid === removeItem.menuItemGuid) {
				pendingItems?.items?.splice(itemDetailIndex, 1);
			}
			pendingItems = pendingItems.items;
			const modLevel = (modLevelItem: IModifierDetails[]) => {
				modLevelItem.forEach((item2: IModifierDetails) => {
					modifierPrice = modifierPrice + Number(item2.modifierPrice) * itemQuantity;

					if (item2.modSets) {
						modsetLevel(item2.modSets);
					}
				});
			};
			const modsetLevel = (modsetLevelItem: IModifierSetDetails[]) => {
				modsetLevelItem.forEach((item1: IModifierSetDetails) => {
					if (item1.mods) {
						modLevel(item1.mods);
					}
				});
			};
			if (removeItem.modSets) {
				modsetLevel(removeItem.modSets);
			}
			updatedPrice = updatedPrice - removedItemPrice - modifierPrice;
			const updatedOrderDetail = JSON.stringify({
				order: { totalPrice: updatedPrice, items: pendingItems },
				shopName: merchantName,
				table: tableRef,
			});
			localStorage.setItem('orderDetail', updatedOrderDetail);
			setOrderDetail(JSON.parse(updatedOrderDetail));
			localStorage.setItem('cartItems', JSON.stringify({ totalPrice: updatedPrice, items: pendingItems }));
		} else {
			localStorage.removeItem('orderDetail');
			localStorage.removeItem('cartItems');
			localStorage.removeItem('storedSessionId');
			navigate(`/${storeId}/homePage`);
		}
	};

	return isOrderDetailViewActive ? (
		<>
			{showLoaderIcon ? (
				<Spinner />
			) : apiErrorMessage.MESSAGE?.length > 0 ? (
				<ErrorModal
					handleDismiss={handleErrorClick}
					typeOfError={apiErrorMessage.TITLE}
					errorMsg={apiErrorMessage.MESSAGE}
					showButton={true}
				/>
			) : (
				<>
					<div className={orderDetailStyles.orderDetailContainer} id="orderDetailContainer">
						<div className={orderDetailStyles.orderDetailHeading}>
							<div className={orderDetailStyles.headingTitle}>
								{isOpenTabView ? labels.VIEW_OPEN_TAB_HEADING : labels.REVIEW_ORDER_HEADING}
							</div>
							<Button variant="text" className={orderDetailStyles.backButton} onClick={handleBackClick}>
								<CloseIcon color="action" />
							</Button>
						</div>
						<div className={orderDetailStyles.ticketContainer}>
							<div className={orderDetailStyles.ticketRow}>
								<InfoIcon className={orderDetailStyles.infoIcon} />
								<Typography className={orderDetailStyles.infoText}>
									{isOpenTabView ? labels.VIEW_OPEN_TAB_INFO_LABEL : labels.REVIEW_ORDER_INFO_LABEL}
								</Typography>
							</div>
						</div>
						<div 
							className={`${orderDetailStyles.detailContainer}
							${isOpenTabView ? orderDetailStyles.marginBottom : ''}
						`}>
							<div className={orderDetailStyles.itemDetailsContainer}>
								{isOpenTabView
									? viewTabDetails?.map((itemDetail, index) => itemsRenderDom(itemDetail, index))
									: orderDetail?.order?.items?.map((itemDetail, index) =>
										itemsRenderDom(itemDetail, index)
									)}
							</div>
						</div>
						<div className={orderDetailStyles.footer}>
							{isOpenTabView ? (
								<div className={orderDetailStyles.orderActions}>
									<Button
										id="orderMoreBtn"
										color="primary"
										fullWidth
										className={`${orderDetailStyles.orderMoreBtn} ${orderDetailStyles.btn} fluid`}
										onClick={handleOrderView}
									>
										Order More
									</Button>
									{isQrPayEnabled && (
										<Button
											id="payCheckButton"
											color="secondary"
											fullWidth
											className={`${orderDetailStyles.payBtn} ${orderDetailStyles.btn}`}
											onClick={handlePayView}
										>
											{isLoading && <LoaderIcon />}
											Pay
										</Button>
									)}
								</div>
							) : (
								<Footer
									id="Place Order"
									externalStyle={orderDetailStyles.placeOrderBackground}
									loaderStatus={isLoading}
									itemPrice={totalPriceInCart}
									labelText="Start Tab"
									showQuantityDetail={false}
									addToOrderInnerWithoutParams={placeOrder}
								/>
							)}
						</div>
					</div>
					{toastMessage?.length > 0 && <Toast message={toastMessage} />}
				</>
			)}
		</>
	) : (
		storeId ? (
			<SelectTicketView
				storeId={storeId}
				merchantName={orderDetail.shopName}
				merchantLogoUrl={merchantLogo}
			/>
		) : null
	);
};
export default OrderDetail;
